import {
  collection,
  doc,
  increment,
  updateDoc,
  writeBatch,
  arrayUnion,
  serverTimestamp,
  type CollectionReference,
  type FirestoreError,
} from "firebase/firestore";
import { firestore } from "@/helpers/firebase/firebase";
import { photoDocumentConverter } from "@kekkle/shared/convertors/web/photo-document.converter";
import type { PhotoDocument } from "@kekkle/shared/types/documents/photo-document/photo-document";
import { handleFirestoreException } from "@/helpers/exceptions/handler";
import {
  countDocuments,
  withUpdatedAt,
  subscribeToAllDocuments,
  subscribeToDocumentChanges,
  type DeltaSyncSnapshot,
  type FullSyncSnapshot,
} from "@/helpers/firestore/DeltaSyncService";
import { generateTrigrams } from "@/helpers/utils/trigrams";

/**
 * Return the photos collection reference with converter
 */
function photosCollection(groupId: string): CollectionReference<PhotoDocument> {
  return collection(firestore, `groups/${groupId}/photos`).withConverter(
    photoDocumentConverter,
  );
}

/**
 * Listens to every photo in a group, ordered by timestamp descending.
 * Used for the first (or a recovery) full sync; see DeltaSyncService.
 * Returns an unsubscribe function.
 */
export function subscribeToAllPhotos(
  groupId: string,
  callbacks: {
    onSnapshot: (snapshot: FullSyncSnapshot<PhotoDocument>) => void;
    onError: (error: FirestoreError) => void;
  },
): () => void {
  return subscribeToAllDocuments(photosCollection(groupId), callbacks);
}

/**
 * Listens to photos whose updated_at is newer than the cursor (epoch ms).
 * Returns an unsubscribe function.
 */
export function subscribeToPhotoChanges(
  groupId: string,
  cursor: number,
  callbacks: {
    onSnapshot: (snapshot: DeltaSyncSnapshot<PhotoDocument>) => void;
    onError: (error: FirestoreError) => void;
  },
): () => void {
  return subscribeToDocumentChanges(
    photosCollection(groupId),
    cursor,
    callbacks,
  );
}

/**
 * Exact number of photos in a group, counted on the server.
 * Used to detect deletes that a delta listener cannot see.
 */
export async function countPhotos(groupId: string): Promise<number> {
  return countDocuments(photosCollection(groupId));
}

/**
 * Generates the id for a new photo without writing anything, so the photo
 * can be shown locally before `addPhoto` is confirmed.
 */
export function createPhotoId(groupId: string): string {
  return doc(photosCollection(groupId)).id;
}

/**
 * Adds a new photo to the group, using the id from `createPhotoId`
 */
export async function addPhoto(
  groupId: string,
  data: PhotoDocument,
): Promise<void> {
  try {
    const batch = writeBatch(firestore);
    const photosRef = photosCollection(groupId);
    const newPhotoRef = doc(photosRef, data._id);

    batch.set(newPhotoRef, withUpdatedAt(data));

    const groupCollection = collection(firestore, "groups/");
    const groupRef = doc(groupCollection, groupId);
    batch.update(groupRef, {
      "general_data.number_of_photos": increment(1),
    });

    await batch.commit();
  } catch (e) {
    handleFirestoreException({ e: e as FirestoreError });
    throw e;
  }
}

/**
 * Updates a photo in the group
 */
export async function updatePhoto(
  groupId: string,
  photoId: string,
  data: Partial<Omit<PhotoDocument, "_id">>,
): Promise<void> {
  try {
    const photosRef = photosCollection(groupId);
    const photoRef = doc(photosRef, photoId);

    // Generate trigrams if caption is being updated
    const updateData: Record<string, unknown> = withUpdatedAt(data);
    if (data.caption) {
      updateData.trigrams = generateTrigrams(data.caption);
    }

    await updateDoc(photoRef, updateData);
  } catch (e) {
    handleFirestoreException({ e: e as FirestoreError });
    throw e;
  }
}

/**
 * Deletes a photo from the group
 */
export async function deletePhoto(
  groupId: string,
  photoId: string,
): Promise<void> {
  try {
    const batch = writeBatch(firestore);
    const photosRef = photosCollection(groupId);
    const photoRef = doc(photosRef, photoId);
    const groupCollection = collection(firestore, "groups/");
    const groupRef = doc(groupCollection, groupId);

    batch.delete(photoRef);
    batch.update(groupRef, {
      "general_data.number_of_photos": increment(-1),
    });

    await batch.commit();
  } catch (e) {
    handleFirestoreException({ e: e as FirestoreError });
    throw e;
  }
}

/**
 * Reports a photo and creates a report document
 */
export async function reportPhoto(
  groupId: string,
  photoId: string,
  reporterId: string,
): Promise<void> {
  try {
    const batch = writeBatch(firestore);
    const photosRef = photosCollection(groupId);
    const photoRef = doc(photosRef, photoId);

    // Update photo with reporter UUID
    batch.update(
      photoRef,
      withUpdatedAt({ reported_by: arrayUnion(reporterId) }),
    );

    // Create report document
    const reportsCollection = collection(firestore, "reports");
    const reportRef = doc(reportsCollection);
    batch.set(reportRef, {
      group_id: groupId,
      type: "photo",
      document_id: photoId,
      created_at: serverTimestamp(),
      created_by: reporterId,
      solved: false,
      solved_at: null,
    });

    await batch.commit();
  } catch (e) {
    handleFirestoreException({ e: e as FirestoreError });
    throw e;
  }
}
